import {createBuffer, formatElapsed, formatWall, payloadForFlush, QA_HZ} from "./ar_qa.js"

const QA_BATCH_MS = 2500

/**
 * The unload fallback's URL.
 *
 * `ARQAController.stop/2` gates on `QA.capture_allowed?(event, params["diag"])`,
 * so the flag has to be on the URL; `?diag=true` is also what the recorder was
 * started with. Exported so the test can pin it — the failure mode is silent
 * (a 403 that only shows up as a row closed by the next boot sweep).
 */
export function stopUrl(slug) {
  const s = encodeURIComponent(slug || "")
  return `/e/${s}/qa/stop?diag=true`
}

export function mosaicQaState(hook) {
  const map = hook?.map
  const el = hook?.el
  const slotNative = num(el?.dataset.slotNativeZoom, 9)
  const tilePx = num(el?.dataset.tilePx, 256)
  const slotCols = num(el?.dataset.slotCols, 512)
  const slotRows = num(el?.dataset.slotRows, 768)
  const zoom = map && typeof map.getZoom === "function" ? map.getZoom() : null
  let cx = null
  let cy = null
  if (map && typeof map.getCenter === "function" && typeof map.project === "function") {
    const center = map.getCenter()
    if (center) {
      const point = map.project(center, slotNative)
      cx = point.x
      cy = point.y
    }
  }
  const slotX = Number.isFinite(cx) ? Math.floor(cx / tilePx) : null
  const slotY = Number.isFinite(cy) ? Math.floor(cy / tilePx) : null
  const doc = typeof document !== "undefined" ? document : null
  const tour = doc?.getElementById("mosaic-tour")
  const hint = doc?.getElementById("mosaic-hint")
  const warming = doc?.getElementById("mosaic-warming")
  const cellPx =
    Number.isFinite(zoom) && tilePx > 0 ? tilePx * 2 ** (zoom - slotNative) : null
  const diving =
    typeof hook?.getDiving === "function" ? Boolean(hook.getDiving()) : Boolean(hook?._divingQa)
  // Whether the lookahead ring was armed. A mosaic walk that pans and still
  // blurs with `prefetch: true` is an arm-timing defect; one that blurs with
  // `prefetch: false` never built the ring. Without this field a recording
  // cannot tell the two apart.
  const prefetch =
    el?.dataset.prefetch === "1" || Boolean(hook?._lookahead?.active)
  return {
    zoom,
    cx,
    cy,
    slotX: wrapSlot(slotX, slotCols),
    slotY: wrapSlot(slotY, slotRows),
    cellPx,
    tour: Boolean(tour && !tour.hidden && !tour.hasAttribute("hidden")),
    hint: Boolean(hint && !hint.hidden && !hint.hasAttribute("hidden")),
    warming: Boolean(warming && !warming.hidden && !warming.hasAttribute("hidden")),
    seam: Boolean(el?.querySelector(".mosaic-seam")),
    pointers: Number(hook?._pointerCount) || 0,
    pinching: Boolean(hook?._pinching),
    diving,
    prefetch,
    ring: Number(hook?._lookahead?.ring) || 0,
  }
}

export function formatMosaicHud(state) {
  const elapsed = formatElapsed(state.elapsedMs)
  const wall = formatWall(state.now)
  const z = Number.isFinite(state.zoom) ? state.zoom.toFixed(2) : "—"
  const cell = Number.isFinite(state.cellPx) ? `${Math.round(state.cellPx)}px` : "—"
  const slot =
    Number.isFinite(state.slotX) && Number.isFinite(state.slotY)
      ? `${state.slotX},${state.slotY}`
      : "—"
  const flags = [
    state.tour && "tour",
    state.hint && "hint",
    state.warming && "warm",
    state.seam && "seam",
    state.pinching && "pinch",
    state.diving && "dive",
    state.prefetch && `pf${state.ring || ""}`,
  ]
    .filter(Boolean)
    .join(" ")
  return {
    clock: `QA ${elapsed}`,
    wall,
    line: `z=${z} cell=${cell} slot=${slot} ptr=${state.pointers || 0}${flags ? "  " + flags : ""}`,
  }
}

export function sampleFromMosaic(state, t, iso) {
  return {
    t,
    iso,
    surface: "mosaic",
    zoom: finiteOrNull(state.zoom),
    cx: finiteOrNull(state.cx),
    cy: finiteOrNull(state.cy),
    slot_x: finiteOrNull(state.slotX),
    slot_y: finiteOrNull(state.slotY),
    cell_px: finiteOrNull(state.cellPx),
    tour: Boolean(state.tour),
    hint: Boolean(state.hint),
    warming: Boolean(state.warming),
    seam: Boolean(state.seam),
    pointers: Number(state.pointers) || 0,
    pinching: Boolean(state.pinching),
    diving: Boolean(state.diving),
  }
}

export function attachMosaicQa(hook) {
  const root = document.getElementById("mosaic-qa")
  const clockEl = document.getElementById("mosaic-qa-clock")
  const wallEl = document.getElementById("mosaic-qa-wall")
  const lineEl = document.getElementById("mosaic-qa-hud")
  const buf = createBuffer({hz: QA_HZ})
  let clientId = null
  let lastFlushAt = 0
  let tickId = null

  function diagOn() {
    return hook?.el?.dataset?.mosaicDiag === "true"
  }

  function slug() {
    return hook?.el?.dataset?.slug || ""
  }

  function eventId() {
    const n = Number(hook?.el?.dataset?.eventId)
    return Number.isFinite(n) ? n : undefined
  }

  function paint() {
    if (!root) return
    const on = diagOn()
    root.hidden = !on
    if (!on) return
    const hud = formatMosaicHud({
      ...mosaicQaState(hook),
      elapsedMs: buf.elapsed(),
      now: new Date(),
    })
    if (clockEl) clockEl.textContent = hud.clock
    if (wallEl) wallEl.textContent = hud.wall
    if (lineEl) lineEl.textContent = hud.line
  }

  function flush(kind) {
    if (!clientId) return
    if (!buf.recording() && kind !== "stop") return
    const {samples, marks} = payloadForFlush(buf, kind)
    if (kind !== "stop" && samples.length === 0 && marks.length === 0) return
    const payload = {client_id: clientId, samples, marks, surface: "mosaic"}
    if (kind === "stop") {
      hook.pushEvent("qa_stop", payload)
      keepaliveStop(payload)
    } else {
      hook.pushEvent("qa_batch", payload)
    }
    lastFlushAt = Date.now()
  }

  // `stop()` runs from `pagehide` too, when the LiveView socket is already
  // closing and `pushEvent` is dropped on the floor. Without this the last
  // samples are lost and the session stays `recording` until the next boot's
  // orphan sweep. Same fallback as the AR recorder (`ar_qa.js`).
  //
  // The `?diag=true` flag is not optional: `ARQAController.stop/2` gates on
  // `params["diag"]`, so a bare POST is a 403 `qa_off`. `ar_qa.js` appends it
  // in `qaUrl/2`; this fallback did not, so every mosaic walk that ended on
  // `pagehide` was closed by the next boot's orphan sweep instead (44 of 49
  // mosaic rows in the corpus). `stopUrl/1` keeps the two in step.
  function keepaliveStop(payload) {
    const s = slug()
    if (!s) return
    try {
      fetch(stopUrl(s), {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": csrfToken(),
        },
        body: JSON.stringify(payload),
        keepalive: true,
      })
    } catch {
      // unload
    }
  }

  function csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
  }

  function tick() {
    if (!buf.recording()) return
    const state = mosaicQaState(hook)
    const t = buf.elapsed()
    buf.pushRaw(sampleFromMosaic(state, t, new Date().toISOString()))
    if (Date.now() - lastFlushAt >= QA_BATCH_MS) flush("batch")
    paint()
  }

  function start() {
    if (buf.recording() || !diagOn()) return
    clientId = newClientId()
    buf.start()
    lastFlushAt = Date.now()
    hook.pushEvent(
      "qa_start",
      {
        client_id: clientId,
        event_id: eventId(),
        user_agent: navigator.userAgent,
        surface: "mosaic",
      },
      (reply) => {
        if (reply && reply.ok === false) {
          buf.stop()
          clearTick()
          clientId = null
          paint()
          if (lineEl) lineEl.textContent = `QA start failed: ${reply.error || "error"}`
        }
      }
    )
    clearTick()
    tickId = setInterval(tick, 500)
    paint()
  }

  function stop() {
    if (!buf.recording()) return
    const state = mosaicQaState(hook)
    buf.pushRaw(sampleFromMosaic(state, buf.elapsed(), new Date().toISOString()))
    flush("stop")
    buf.stop()
    clearTick()
    paint()
  }

  function clearTick() {
    if (tickId) clearInterval(tickId)
    tickId = null
  }

  function newClientId() {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
    return `m${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`
  }

  // The dev toggle patches `data-mosaic-diag` on the map root without
  // remounting this hook (`#mosaic-root` is `phx-update="ignore"`, so the
  // attribute changes and the children stay). Follow it: a diag run started
  // from the toggle must record exactly like one started from `?diag=true`.
  let wasOn = diagOn()
  const observer =
    typeof MutationObserver === "function" && hook?.el
      ? new MutationObserver(() => {
          const on = diagOn()
          if (on === wasOn) return
          wasOn = on
          if (on) start()
          else stop()
          paint()
        })
      : null
  observer?.observe(hook.el, {attributes: true, attributeFilter: ["data-mosaic-diag"]})

  if (diagOn()) start()
  window.addEventListener("pagehide", stop)

  return {
    paint,
    stop,
    teardown() {
      observer?.disconnect()
      if (buf.recording()) stop()
      window.removeEventListener("pagehide", stop)
      clearTick()
    },
  }
}

function finiteOrNull(n) {
  const x = Number(n)
  return Number.isFinite(x) ? x : null
}

function num(value, fallback) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function wrapSlot(n, size) {
  if (!Number.isFinite(n) || !(size > 0)) return null
  return ((n % size) + size) % size
}
